import React, { useEffect, useState } from "react";
import LazyLoad from "react-lazyload";
import { Helmet } from "react-helmet";
import sanityClient from "../../sanityClient";
import HouseCards from "../../components/HouseCards";
import ribbonCutting from "../../assets/images/how-to-apply/ribbon.webp";
import IncomeTable from "../../components/IncomeTable";
import CTASection from "../../components/CTASection";
import styled from "styled-components";

const Container = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;

  @media (max-width: 768px) {
    padding: 15px;
  }

  @media (max-width: 480px) {
    padding: 10px;
  }
`;

const SectionHeader = styled.h2`
  font-size: 2em;
  text-align: center;
  margin: 20px 0;

  @media (max-width: 768px) {
    font-size: 1.8em;
    margin: 15px 0;
  }

  @media (max-width: 480px) {
    font-size: 1.5em;
    margin: 10px 0;
  }
`;

const Paragraph = styled.p`
  font-size: 1.2em;
  line-height: 1.6;
  margin-bottom: 20px;

  @media (max-width: 768px) {
    font-size: 1.1em;
    margin-bottom: 15px;
  }

  @media (max-width: 480px) {
    font-size: 1em;
    margin-bottom: 10px;
  }
`;

const Image = styled.img`
  width: 100%;
  max-width: 100%;
  border-radius: 8px;
  margin: 20px 0;

  @media (max-width: 768px) {
    margin: 15px 0;
  }

  @media (max-width: 480px) {
    margin: 10px 0;
  }
`;

const IframeContainer = styled.div`
  position: relative;
  width: 100%;
  padding-bottom: 56.25%; /* 16:9 aspect ratio */
  height: 0;
  overflow: hidden;
  margin-bottom: 40px;

  iframe {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }
`;

const List = styled.ol`
  font-size: 1.2em;
  margin: 20px 0;

  @media (max-width: 768px) {
    font-size: 1.1em;
  }

  @media (max-width: 480px) {
    font-size: 1em;
  }

  li {
    margin-bottom: 10px;
    line-height: 1.6;

    ul {
      margin-top: 10px;
      padding-left: 20px;
      list-style-type: disc;
    }

    li {
      margin-bottom: 5px;
    }
  }
`;

const Homeownership = () => {
  const [projects, setProjects] = useState([]);

  const today = new Date();
  const preAppStart = new Date("2025-04-28T00:00:00");
  const preAppEnd = new Date("2025-05-09T23:59:59");

  const isPreApplicationOpen = today >= preAppStart && today <= preAppEnd;

  useEffect(() => {
    sanityClient
      .fetch(
        `*[_type == "build"] | order(completionDate desc)[0...6] {
          _id,
          name,
          description,
          "imageUrl": mainImage.asset->url,
          address,
        }`
      )
      .then((data) => setProjects(data))
      .catch(console.error);
  }, []);

  return (
    <>
      <Helmet>
        <title>Homeownership - Habitat for Humanity Buffalo</title>
        <meta
          name="description"
          content="Learn about Habitat for Humanity Buffalo's Homeownership Program and how you can achieve the dream of owning your own home through our affordable housing initiative."
        />
      </Helmet>
      <Container>
        <h1>Homeownership</h1>
        <SectionHeader className="section-header primary">
          Is it your dream to own your own home?
        </SectionHeader>
        <Paragraph>
          Thank you for your interest in Habitat for Humanity Buffalo and our
          Homeownership Program. For nearly 40 years, Habitat Buffalo has built
          homes and fulfilled dreams in partnership with hardworking individuals
          and families. Through a collaborative effort, Habitat Buffalo works
          towards increasing the affordable housing stock in our community.
          Homes are sold to qualifying partner homebuyers and financed with
          affordable loans.
        </Paragraph>
        <LazyLoad height={200} offset={100}>
          <Image
            src={ribbonCutting}
            alt="Ribbon cutting ceremony for a new home"
          />
        </LazyLoad>

        <SectionHeader className="section-header orange">
          How the Program Works
        </SectionHeader>
        <Paragraph>
          With the support of community partners, donors, and volunteers,
          Habitat Buffalo constructs new homes and renovates existing homes
          alongside Habitat Partner Homebuyers. Our homeownership program is
          designed to assist low-income first-time homebuyers to achieve the
          American dream of homeownership. Habitat homes are sold to partner
          homebuyers at no profit and financed with affordable loans. In
          addition to a down payment and monthly mortgage payments, homeowners
          invest sweat equity hours building their future Habitat home and the
          homes of other Habitat Homebuyers. Interested applicants must submit a
          pre-application questionnaire and meet the following program
          eligibility requirements.
        </Paragraph>
        <LazyLoad height={315} offset={100}>
          <IframeContainer>
            <iframe
              src="https://www.youtube.com/embed/gh60ZsXD2EE"
              title="Overview of Habitat for Humanity's Homeownership Program"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            ></iframe>
          </IframeContainer>
        </LazyLoad>

        <SectionHeader className="section-header green">
          Program Criteria
        </SectionHeader>
        <List className="list">
          <li>
            Homebuyers must be able to demonstrate they can afford a small down
            payment and a monthly mortgage payment.
          </li>
          <li>
            Homebuyers must fall within the minimum 50% AMI and maximum 80% AMI.
          </li>
          <li>
            Homebuyers must have good credit (no recent bankruptcies,
            collections, liens, or judgments).
          </li>
          <li>
            Homebuyers must demonstrate a need for decent and affordable
            housing.
          </li>
          <li>
            Complete Habitat Buffalo’s Family Investment Education Program
            (optional).
          </li>
          <li>
            Homebuyers must be willing to partner with Habitat Buffalo by
            completing the following:
            <ul>
              <li>400 sweat equity hours</li>
              <li>A HUD-certified pre-purchase homebuyer education workshop</li>
            </ul>
          </li>
          <li>
            All household members must be U.S. Citizens or permanent legal
            residents.
          </li>
        </List>
        <IncomeTable />

        <Paragraph>
          *Note: Income guidelines vary by property development and are subject
          to change per the Department of Housing and Urban Development (HUD)
          and the New York State Department of Housing and Community Development
          (HCD).
        </Paragraph>

        {isPreApplicationOpen ? (
          <CTASection
            title="Pre-Applications Now Open"
            description="Pre-applications for Habitat Buffalo's Homeownership Program are open until May 9, 2025. Take the first step toward affordable homeownership by submitting your pre-application today."
            buttonText="Start Your Pre-Application"
            buttonLink="https://habitatbflo.my.site.com/preapp/s/?mc_cid=18cdb01d1a&mc_eid=UNIQID"
          />
        ) : (
          <CTASection
            title="Apply for a Habitat Buffalo Home"
            description="Taking the first step toward homeownership starts with joining our email notification list. This ensures you'll be the first to know when pre-applications open. Once pre-applications become available, you’ll receive an email with instructions on how to apply. Eligible applicants will then be contacted to complete the full application process."
            buttonText="Sign Up For Email Notifications"
            buttonLink="https://habitatbuffalo.us15.list-manage.com/subscribe?u=5d7743596ea119fff30b64404&id=8aa251e4c9"
          />
        )}

        <div className="section">
          <SectionHeader className="section-header orange">
            Current Projects
          </SectionHeader>
          <HouseCards projects={projects} />
        </div>
      </Container>
    </>
  );
};

export default Homeownership;
